import { useState, useCallback, useEffect, useRef } from 'react';
import { useBroadcastChannel } from '@/hooks/useBroadcastChannel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PlayerPage() {
  const [state, setState] = useState({
    x: 50, y: 50,
    radius: 150, zoom: 1,
    fogOpacity: 1, feather: 40,
    mapUrl: '',
    placedTokens: [],
    showGrid: false, gridSize: 50, gridColor: 'white', gridOpacity: 0.1,
    spotlightMode: 'spotlight',
    panOffset: { x: 0, y: 0 },
  });
  const [pings, setPings] = useState([]);
  const containerRef = useRef(null);

  const onMessage = useCallback((data) => {
    if (!data || !data.type) return;
    switch (data.type) {
      case 'MOVE':
        setState(prev => ({ ...prev, x: data.x, y: data.y }));
        break;
      case 'RESIZE':
        setState(prev => ({ ...prev, radius: data.radius }));
        break;
      case 'ZOOM':
        setState(prev => ({ ...prev, zoom: data.zoom }));
        break;
      case 'FOG_OPACITY':
        setState(prev => ({ ...prev, fogOpacity: data.fogOpacity }));
        break;
      case 'FEATHER':
        setState(prev => ({ ...prev, feather: data.feather }));
        break;
      case 'MAP_UPDATE':
        setState(prev => ({ ...prev, mapUrl: data.mapUrl }));
        break;
      case 'TOKENS_UPDATE':
        setState(prev => ({ ...prev, placedTokens: data.placedTokens || [] }));
        break;
      case 'PING': {
        const pingId = data.id || Date.now();
        setPings(prev => [...prev, { id: pingId, x: data.x, y: data.y }]);
        setTimeout(() => setPings(prev => prev.filter(p => p.id !== pingId)), 1200);
        break;
      }
      case 'GRID_SIZE':
        setState(prev => ({ ...prev, gridSize: data.gridSize }));
        break;
      case 'GRID_COLOR':
        setState(prev => ({ ...prev, gridColor: data.gridColor }));
        break;
      case 'GRID_OPACITY':
        setState(prev => ({ ...prev, gridOpacity: data.gridOpacity }));
        break;
      case 'SPOTLIGHT_MODE':
        setState(prev => ({ ...prev, spotlightMode: data.spotlightMode }));
        break;
      case 'SPOTLIGHT_TOGGLE':
        setState(prev => ({ ...prev, spotlightMode: data.spotlightEnabled ? 'spotlight' : 'off' }));
        break;
      case 'FULL_STATE':
        setState({
          x: data.x ?? 50, y: data.y ?? 50,
          radius: data.radius ?? 150, zoom: data.zoom ?? 1,
          fogOpacity: data.fogOpacity ?? 1, feather: data.feather ?? 40,
          mapUrl: data.mapUrl ?? '',
          placedTokens: data.placedTokens ?? [],
          showGrid: data.showGrid ?? false,
          gridSize: data.gridSize ?? 50, gridColor: data.gridColor ?? 'white',
          gridOpacity: data.gridOpacity ?? 0.1,
          spotlightMode: data.spotlightMode ?? (data.spotlightEnabled === false ? 'off' : 'spotlight'),
          panOffset: data.panOffset ?? { x: 0, y: 0 },
        });
        break;
      default:
        break;
    }
  }, []);

  useBroadcastChannel(onMessage);

  useEffect(() => {
    const bc = new BroadcastChannel('fog-of-war-channel');
    bc.postMessage({ type: 'REQUEST_STATE' });
    return () => bc.close();
  }, []);

  const mapImageUrl = state.mapUrl
    ? (state.mapUrl.startsWith('http') ? state.mapUrl : `${API}/files/${state.mapUrl}`)
    : '';

  const gridRgb = state.gridColor === 'white' ? '255,255,255'
    : state.gridColor === 'gray' ? '128,128,128' : '0,0,0';

  const fogClass = state.spotlightMode === 'off'
    ? 'player-fog spotlight-off'
    : state.spotlightMode === 'fullbright'
      ? 'player-fog fullbright'
      : 'player-fog';

  return (
    <div className="player-page" ref={containerRef} data-testid="player-page">
      {mapImageUrl ? (
        <>
          {/* Unified world transform — map, grid, and tokens move/scale together */}
          <div
            className="absolute inset-0"
            style={{
              transform: `translate(${state.panOffset?.x || 0}px, ${state.panOffset?.y || 0}px) scale(${state.zoom})`,
              transformOrigin: 'center center',
            }}
            data-testid="player-world"
          >
            <img
              src={mapImageUrl}
              alt="Battle Map"
              className="w-full h-full object-contain select-none"
              draggable={false}
              data-testid="player-map-image"
            />

            {state.showGrid && (
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  backgroundImage: `linear-gradient(rgba(${gridRgb},${state.gridOpacity}) 1px, transparent 1px), linear-gradient(90deg, rgba(${gridRgb},${state.gridOpacity}) 1px, transparent 1px)`,
                  backgroundSize: `${state.gridSize || 50}px ${state.gridSize || 50}px`,
                }}
              />
            )}

            {(state.placedTokens || []).map((token, index) => (
              <div
                key={token.id || index}
                className="absolute pointer-events-none"
                style={{
                  left: `${token.x}%`, top: `${token.y}%`,
                  width: `${token.width}px`, height: `${token.height}px`,
                  transform: `translate(-50%, -50%) scale(${token.scale || 1}) rotate(${token.rotation || 0}deg)`,
                  borderRadius: '6px',
                }}
                data-testid={`player-token-${index}`}
              >
                <img
                  src={token.image_url}
                  alt={token.name || 'token'}
                  className="w-full h-full object-contain rounded"
                  draggable={false}
                />
              </div>
            ))}
          </div>

          {/* Fog overlay — viewport space, rendered AFTER world div so it's on top */}
          <div
            className={fogClass}
            style={{
              '--fog-x': `${state.x}%`,
              '--fog-y': `${state.y}%`,
              '--fog-radius': `${state.radius}px`,
              '--fog-feather': `${state.feather}px`,
              '--fog-opacity': state.fogOpacity,
            }}
            data-testid="player-fog"
          />

          {/* Pings — viewport space */}
          {pings.map(ping => (
            <div key={ping.id} className="ping-effect" style={{ left: `${ping.x}%`, top: `${ping.y}%` }}>
              <div className="ping-ring" /><div className="ping-ring" /><div className="ping-ring" />
            </div>
          ))}
        </>
      ) : (
        <div className="flex items-center justify-center h-full" data-testid="player-waiting">
          <div className="text-center animate-fade-in">
            <div className="w-16 h-16 mx-auto mb-6 border-2 border-emerald-500/30 rounded-full flex items-center justify-center">
              <div className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse" />
            </div>
            <p className="text-zinc-400 text-lg" style={{ fontFamily: 'Cinzel, serif' }}>
              Awaiting the Game Master
            </p>
            <p className="text-zinc-600 text-sm mt-2">
              The map will appear when the session begins
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
